import { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Spin,
  DatePicker
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "../json/appConstant.json";

const Wards = ({ isDarkMode }) => {
  const [form] = Form.useForm();
  const [wardsData, setWardsData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const { jsonApi } = useApis();

  useEffect(() => {
    fetchWards();
  }, [pagination.current, pagination.pageSize, search]);

  const fetchWards = async () => {
    setIsDataLoading(true);
    const params = {
      page: pagination.current,
      pageSize: pagination.pageSize,
      search,
    };

    const response = await jsonApi(appConstant.WardAllData, params, {}, null);
    if (response?.data?.code === 200 && response.data.status === "success") {
      const data = response.data.data.wards || response.data.data;
      setWardsData(
        data.map((ward) => ({
          key: ward.Id,
          ...ward,
        }))
      );
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.total || data.length,
      }));
    }
    setIsDataLoading(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        WardName: values.WardName,
        WardNameH: values.WardNameH,
        WardCode: values.WardCode,
        NagarPalikaID: values.NagarPalikaID,
        ZoneID: values.ZoneID,
        TransDate: new Date().toISOString(),
        IsActive: true,
      };

      const response = await jsonApi(appConstant.createWard, null, payload, null);
      if (response?.data?.code === 200) {
        message.success("Ward added successfully");
        fetchWards();
        setIsModalVisible(false);
        form.resetFields();
      } else {
        message.error(response?.data?.message || "Failed to add ward");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "S.No",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Ward Name",
      dataIndex: "WardName",
      key: "WardName",
    },
    {
      title: "Ward Name (Hindi)",
      dataIndex: "WardNameH",
      key: "WardNameH",
    },
    {
      title: "Ward Code",
      dataIndex: "WardCode",
      key: "WardCode",
    },

    {
      title: "Nagar Palika Name",
      dataIndex: ["user", "name"],
      key: "NagarPalikaName",
    },
    {
      title: "Zone ID",
      dataIndex: "ZoneID",
      key: "ZoneID",
    },
    {
      title: "Trans Date",
      dataIndex: "TransDate",
      key: "TransDate",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Is Active",
      dataIndex: "IsActive",
      key: "IsActive",
      render: (val) => (val ? "Yes" : "No"),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ color: isDarkMode ? "#fff" : undefined }}>Wards</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
        >
          Add Ward
        </Button>
      </div>

      <Input
        placeholder="Search wards..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPagination((prev) => ({ ...prev, current: 1 }));
        }}
        suffix={
          search && (
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                setSearch("");
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          )
        }
        style={{ marginBottom: 16, width: 300 }}
      />

      <Card style={{ background: isDarkMode ? "#1f1f1f" : "#ffffff" }}>
        <Spin spinning={isDataLoading}>
          <Table
            columns={columns}
            dataSource={wardsData}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
              },
            }}
          />
        </Spin>
      </Card>

      <Modal
        title="Add Ward"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Add Ward
          </Button>,
        ]}
        width={600}
      >

        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item
            name="WardName"
            label="Ward Name"

            rules={[
              { required: true, message: "Please enter Ward Name" },
              {
                pattern: /^[A-Za-z\s]+$/,
                message: "Ward Name can only contain letters and spaces",
              },
            ]} >


            <Input placeholder="Enter Ward Name" maxLength={50} />
          </Form.Item>

          <Form.Item
            name="WardNameH"
            label="Ward Name (Hindi)"

            rules={[
              { required: true, message: "Please enter Ward Name in Hindi" },
            ]} >

            <Input placeholder="Enter Ward Name in Hindi" maxLength={50} />
          </Form.Item>


          <Form.Item
            name="WardCode"
            label="Ward Code"

            rules={[
              { required: true, message: "Please enter Ward Code" },
            ]} >

            <Input placeholder="Enter Ward Code " maxLength={50} />
          </Form.Item>



          <Form.Item
            name="ZoneID"
            label="Zone ID"
            rules={[{ required: true, message: "Please enter Zone ID" }]}
          >
            <Input type="number" placeholder="Enter Zone ID" maxLength={50} />
          </Form.Item>

          <Form.Item
            name="Transdate"
            label="Transaction Date"
            rules={[{ required: true, message: "Please select a transaction date" }]}
          >
            <DatePicker style={{ width: "100%" }} placeholder="Select Transaction Date" />
          </Form.Item>
        </Form>

      </Modal>
    </div>
  );
};

export default Wards;
